import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./Components/ProtectedRoute";
import Layout from "./Layout";
import { ChatContextProvider } from "./context/chatContext";
import AdminLogin from "./Components/NewEnhancedLogin";
import { AuthBootstrap } from "./authClient";

// Dashboard Pages
import HomePage from "./Pages/dashboards/HomePage";
import PrincipalDashboard from "./Pages/dashboards/PrincipalDashboard";
import ManagementDashboard from "./Pages/dashboards/ManagementDashboard";
import HODDashboard from "./Pages/dashboards/HODDashboard";

// User Management Pages
import StudentView from "./Pages/user-management/StudentView";
import TeacherView from "./Pages/user-management/TeacherView";
import ParentView from "./Pages/user-management/ParentView";
import UserView from "./Pages/user-management/UserView";
import ViewTechLeaders from "./Pages/user-management/ViewTechLeaders";
import ViewHODs from "./Pages/user-management/ViewHODs";
import ViewFinance from "./Pages/user-management/ViewFinance";
import ViewCocurricular from "./Pages/user-management/ViewCocurricular";
import FinanceDashboardPage from "./Pages/finance/DashboardPage";
import AdmissionBooster from "./Pages/admissions/AdmissionBooster";

// Curriculum Pages
import LessonPlanManagement from "./Pages/curriculum/LessonPlanManagement";
import UnitPlanManagement from "./Pages/curriculum/UnitPlanManagement";
import SchemeOfWorkManagement from "./Pages/curriculum/SchemeOfWorkManagement";
import AnnualPedagogicalPlan from "./Pages/curriculum/AnnualPedagogicalPlan";
import CompetencyManagement from "./Pages/curriculum/CompetencyManagement";
import SchoolDevelopmentDashboard from "./Pages/curriculum/SchoolDevelopmentDashboard";
import ProgressDashboard from "./Pages/dashboards/ProgressDashboard";
import NEPNCFPlanner from "./Pages/curriculum/SQAAF";
import SQAAFPlansManager from "./Pages/curriculum/SQAAFPlansManager";
import HPCManagement from "./Pages/curriculum/HPCManagement";
import ClassManagement from "./Pages/curriculum/ClassManagement";
import CBSECirculars from "./Pages/curriculum/CBSECirculars";
import DepartmentManagement from "./Pages/departments/DepartmentManagement";

// Assessment Pages
import AssessmentManagement from "./Pages/assessment/AssessmentManagement";
import StudentReport from "./Pages/assessment/StudentReport";
import Leaderboard from "./Pages/assessment/Leaderboard";

// Calendar Pages
import CalendarManagement from "./Pages/calendar/CalendarManagement";
import EisenhowerMatrix from "./Pages/calendar/EisenhowerMatrix";

// Feedback Pages
import ViewFeedbackPage from "./Pages/feedback/ViewFeedbackPage";

// Shared Pages
import ComingSoon from "./Pages/shared/ComingSoon";

// ACPP — Annual Curriculum, Pedagogy & Assessment Plan
import ACPPPlansManager from "./Pages/acpp/PlansManager";
import ACPPApprovals from "./Pages/acpp/approvals/ACPPApprovals";
import ACPPModulePage from "./Pages/acpp/ACPPModulePage";

// Growth Overview
import GrowthOverview from "./Pages/growth/GrowthOverview";
import GrowthAchievementManagement from "./Pages/growth/GrowthAchievementManagement";
import AcademicTranscriptManagement from "./Pages/growth/AcademicTranscriptManagement";
import StudentReportsCentre from "./Pages/growth/StudentReportsCentre";
import StudentReportHistory from "./Pages/growth/StudentReportHistory";

// Profile Page (separate)
import ProfilePage from "./Pages/ProfilePage";

// SDP (School Development Portal)
import SDPDashboard from "./Pages/sdp/Dashboard";
import SDPQuickStartLanding from "./Pages/sdp/QuickStartLanding";
import SDPInstitutionalIdentity from "./Pages/sdp/InstitutionalIdentity";
import SDPSchoolInformation from "./Pages/sdp/SchoolInformation";
import SDPExpenditure from "./Pages/sdp/Expenditure";
import SDPTeamsCommittee from "./Pages/sdp/TeamsCommittee";
import SDPMarketDynamics from "./Pages/sdp/MarketDynamics";
import SDPSWOT from "./Pages/sdp/SWOT";
import SDPGoals from "./Pages/sdp/Goals";
import SDPActionPlan from "./Pages/sdp/ActionPlan";
import SDPMonitoring from "./Pages/sdp/Monitoring";
import SDPReportsSummary from "./Pages/sdp/ReportsSummary";
import SDPCompleteSchoolView from "./Pages/sdp/CompleteSchoolView";
import SDPPlansManager from "./Pages/sdp/PlansManager";

function App() {
    return (
        <ChatContextProvider>
            <AuthBootstrap>
                <Router>
                    <Routes>
                        {/* Login route with enhanced multi-tenant login */}
                        <Route path="/" element={
                            <AdminLogin
                                panelType="admin"
                                title="HPC CogniApp - For Principals"
                                redirectPath="/HomePage"
                            />
                        } />
                        <Route path="/login" element={
                            <AdminLogin
                                panelType="admin"
                                title="HPC CogniApp - For Principals"
                                redirectPath="/HomePage"
                            />
                        } />

                        {/* Every protected route below shares ONE Layout instance (Header +
                            Sidebar), mounted here once instead of per-page. Before this,
                            each page wrapped itself in its own <Layout>, so React tore down
                            and rebuilt the whole sidebar (losing scroll/collapse/search state,
                            with a visible flash) on every single navigation. Now navigating
                            between pages only swaps the <Outlet/> content. */}
                        <Route element={<Layout />}>

                        {/* Protected routes with admin role requirement */}
                        <Route path="/HomePage" element={
                            <ProtectedRoute requiredRole="admin">
                                <HomePage />
                            </ProtectedRoute>
                        } />

                        <Route path="/dashboard" element={
                            <ProtectedRoute requiredRole="admin">
                                <HomePage />
                            </ProtectedRoute>
                        } />

                        {/* <Route path="/SchoolDevelopment" element={
                            <ProtectedRoute requiredRole="admin">
                                <SchoolDevelopmentDashboard />
                            </ProtectedRoute>
                        } /> */}

                        {/* <Route path="/AnnualPedagogicalPlan" element={
                            <ProtectedRoute requiredRole="admin">
                                <AnnualPedagogicalPlan />
                            </ProtectedRoute>
                        } /> */}

                        <Route path="/finance/dashboard" element={
                            <ProtectedRoute allowedRoles={['finance', 'admin', 'management']}>
                                <FinanceDashboardPage />
                            </ProtectedRoute>
                        } />

                        {/* Admission Booster is currently a frontend-only UI prototype. */}
                        <Route path="/admission-booster" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <AdmissionBooster />
                            </ProtectedRoute>
                        } />

                        <Route path="/ACPP" element={
                            <Navigate to="/acpp/module1" replace />
                        } />
                        <Route path="/acpp" element={
                            <Navigate to="/acpp/module1" replace />
                        } />
                        <Route path="/acpp/module1" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module1" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module2" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module2" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module3" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module3" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module4" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module4" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module5" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module5" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module6" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module6" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module7" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module7" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module8" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module8" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module9" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module9" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module10" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module10" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module11" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module11" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module12" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module12" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/module13" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPModulePage moduleKey="module13" />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/plans" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPPlansManager />
                            </ProtectedRoute>
                        } />
                        <Route path="/acpp/approvals" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <ACPPApprovals />
                            </ProtectedRoute>
                        } />

                        <Route path="/approvals/assessments" element={<Navigate to="/HODDashboard?tab=assessments" replace />} />

                        <Route path="/CalendarManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <CalendarManagement />
                            </ProtectedRoute>
                        } />

                        <Route path="/EisenhowerMatrix" element={
                            <ProtectedRoute requiredRole="admin">
                                <EisenhowerMatrix />
                            </ProtectedRoute>
                        } />

                        <Route path="/LessonPlanManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <LessonPlanManagement />
                            </ProtectedRoute>
                        } />

                        <Route path="/AssessmentManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <AssessmentManagement />
                            </ProtectedRoute>
                        } />

                        <Route path="/CompetencyManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <CompetencyManagement />
                            </ProtectedRoute>
                        } />

                        {/* <Route path="/SchemeOfWorkManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <SchemeOfWorkManagement />
                            </ProtectedRoute>
                        } /> */}

                        {/* <Route path="/UnitPlanManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <UnitPlanManagement />
                            </ProtectedRoute>
                        } /> */}

                        <Route path="/SQAAF" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SQAAFPlansManager />
                            </ProtectedRoute>
                        } />
                        <Route path="/SQAAF/:year" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <NEPNCFPlanner />
                            </ProtectedRoute>
                        } />

                        <Route path="/HPCManagement" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <HPCManagement />
                            </ProtectedRoute>
                        } />
                        <Route path="/GrowthAchievementReports" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <GrowthAchievementManagement />
                            </ProtectedRoute>
                        } />
                        <Route path="/AcademicTranscriptReports" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <AcademicTranscriptManagement />
                            </ProtectedRoute>
                        } />
                        <Route path="/StudentReportsCentre" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <StudentReportsCentre />
                            </ProtectedRoute>
                        } />
                        <Route path="/StudentReportsCentre/:studentId" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <StudentReportHistory />
                            </ProtectedRoute>
                        } />

                        <Route path="/ClassManagement" element={
                            <ProtectedRoute requiredRole="admin">
                                <ClassManagement />
                            </ProtectedRoute>
                        } />

                        <Route path="/CBSECirculars" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'hod']}>
                                <CBSECirculars />
                            </ProtectedRoute>
                        } />

                        <Route path="/DepartmentManagement" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <DepartmentManagement />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewstudents" element={
                            <ProtectedRoute requiredRole="admin">
                                <StudentView />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewparents" element={
                            <ProtectedRoute requiredRole="admin">
                                <ParentView />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewteachers" element={
                            <ProtectedRoute requiredRole="admin">
                                <TeacherView />
                            </ProtectedRoute>
                        } />

                        <Route path="/users" element={
                            <ProtectedRoute requiredRole="admin">
                                <UserView />
                            </ProtectedRoute>
                        } />

                        <Route path="/report" element={
                            <ProtectedRoute requiredRole="admin">
                                <StudentReport />
                            </ProtectedRoute>
                        } />

                        <Route path="/leaderboard" element={
                            <ProtectedRoute requiredRole="admin">
                                <Leaderboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/analytics" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <ProgressDashboard />
                            </ProtectedRoute>
                        } />

                        <Route path="/GrowthOverview" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <GrowthOverview />
                            </ProtectedRoute>
                        } />

                        <Route path="/feedback" element={
                            <ProtectedRoute requiredRole="admin">
                                <ViewFeedbackPage />
                            </ProtectedRoute>
                        } />

                        <Route path="/Profile" element={
                            <ProtectedRoute requiredRole="admin">
                                <ProfilePage />
                            </ProtectedRoute>
                        } />

                        {/* SDP - School Development Portal (flattened into the single shared
                            Layout above, so entering/leaving SDP doesn't remount it either) */}
                        <Route path="/sdp/dashboard" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPDashboard />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/quick-start" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPQuickStartLanding />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/institutional-identity" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPInstitutionalIdentity />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/school-information" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPSchoolInformation />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/expenditure" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPExpenditure />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/teams-committees" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPTeamsCommittee />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/market-dynamics" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPMarketDynamics />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/diagnosis" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <NEPNCFPlanner noLayout />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/swot" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPSWOT />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/goals" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPGoals />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/action-plans" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPActionPlan />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/monitoring" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPMonitoring />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/reports-summary" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPReportsSummary />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/complete-school-view" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPCompleteSchoolView />
                            </ProtectedRoute>
                        } />
                        <Route path="/sdp/plans" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <SDPPlansManager />
                            </ProtectedRoute>
                        } />

                        {/* Coming Soon placeholder for new roles */}
                        <Route path="/ComingSoon" element={
                            <ProtectedRoute requiredRole="admin">
                                <ComingSoon />
                            </ProtectedRoute>
                        } />

                        {/* Management Dashboard - for management role */}
                        <Route path="/ManagementDashboard" element={
                            <ProtectedRoute allowedRoles={['admin', 'management']}>
                                <ManagementDashboard />
                            </ProtectedRoute>
                        } />

                        {/* HOD Dashboard - for hod role, plus admin/management for
                            the second-stage lesson-plan approval tab it also hosts */}
                        <Route path="/HODDashboard" element={
                            <ProtectedRoute allowedRoles={['admin', 'hod', 'management']}>
                                <HODDashboard />
                            </ProtectedRoute>
                        } />

                        {/* Staff Management Pages - Admin, Management, and Tech Leader can access */}
                        <Route path="/viewtechleaders" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'tech_leader']}>
                                <ViewTechLeaders />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewhods" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'tech_leader']}>
                                <ViewHODs />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewfinance" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'tech_leader']}>
                                <ViewFinance />
                            </ProtectedRoute>
                        } />

                        <Route path="/viewcocurricular" element={
                            <ProtectedRoute allowedRoles={['admin', 'management', 'tech_leader']}>
                                <ViewCocurricular />
                            </ProtectedRoute>
                        } />

                        {/* Catch-all route - redirect to dashboard if authenticated, login if not */}
                        <Route path="*" element={
                            <ProtectedRoute requiredRole="admin">
                                <HomePage />
                            </ProtectedRoute>
                        } />

                        </Route>
                    </Routes>
                </Router>
            </AuthBootstrap>
        </ChatContextProvider >
    );
}

export default App;
